import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { uploadVideoToCloudinary } from '../../config/cloudinary';
import { convertToEmbedUrl, isYouTubeUrl } from '../../utils/videoUtils';
import '../../styles/admin.css';

const ManageVideos = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const videosRef = collection(db, 'videos');
      const q = query(videosRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const videosData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setVideos(videosData);
    } catch (error) {
      console.error('Error fetching videos:', error);
      alert('Error fetching videos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type.startsWith('video/')) {
        setSelectedFile(file);
      } else {
        alert('Please select a valid video file');
        e.target.value = '';
      }
    }
  };

  const handleUploadVideo = async () => {
    if (!selectedFile) {
      alert('Please select a video file');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const uploadResult = await uploadVideoToCloudinary(selectedFile, (progress) => {
        setUploadProgress(progress);
      });

      setFormData({
        ...formData,
        url: uploadResult.url,
      });

      setSelectedFile(null);
      alert('Video uploaded successfully to Cloudinary!');
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Error uploading video. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.url) {
      alert('Please upload a video or provide a video URL');
      return;
    }

    if (!formData.title.trim()) {
      alert('Please provide a video title');
      return;
    }

    try {
      setLoading(true);

      if (editingVideo) {
        const videoRef = doc(db, 'videos', editingVideo.id);
        await updateDoc(videoRef, {
          title: formData.title,
          description: formData.description,
          url: formData.url,
          updatedAt: serverTimestamp(),
        });
        alert('Video updated successfully!');
      } else {
        await addDoc(collection(db, 'videos'), {
          title: formData.title,
          description: formData.description,
          url: formData.url,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        alert('Video added successfully!');
      }

      setFormData({ title: '', description: '', url: '' });
      setEditingVideo(null);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Error saving video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      url: video.url,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'videos', videoId));
      alert('Video deleted successfully!');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error deleting video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ title: '', description: '', url: '' });
    setEditingVideo(null);
    setSelectedFile(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button onClick={() => navigate('/admin')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1 className="admin-title">Manage Videos</h1>
      </div>

      <div className="admin-container">
        <div className="admin-card">
          <h2 className="card-title">
            {editingVideo ? 'Edit Video' : 'Add New Video'}
          </h2>

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label htmlFor="title">Video Title *</label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter video title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter video description"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="videoFile">Upload Video to Cloudinary</label>
              <input
                type="file"
                id="videoFile"
                accept="video/*"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {selectedFile && (
                <div className="file-info">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
              {selectedFile && !uploading && (
                <button
                  type="button"
                  onClick={handleUploadVideo}
                  className="btn btn-secondary"
                  style={{ marginTop: '0.5rem' }}
                >
                  Upload to Cloudinary
                </button>
              )}
              {uploading && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
                  </div>
                  <p>Uploading... {uploadProgress}%</p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="url">Video URL *</label>
              <input
                type="url"
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="Video URL (auto-filled after upload or enter manually)"
                required
              />
              <small>Upload a video above or paste a direct video URL</small>
            </div>

            {formData.url && (
              <div className="video-preview">
                <label>Preview</label>
                {isYouTubeUrl(formData.url) ? (
                  <iframe
                    src={convertToEmbedUrl(formData.url)}
                    title="Video Preview"
                    style={{ width: '100%', height: '300px', borderRadius: '8px', border: 'none' }}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={formData.url}
                    controls
                    style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
                  />
                )}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
                {loading ? 'Saving...' : editingVideo ? 'Update Video' : 'Add Video'}
              </button>
              {editingVideo && (
                <button type="button" onClick={handleCancel} className="btn btn-secondary">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="admin-card">
          <h2 className="card-title">All Videos ({videos.length})</h2>

          {loading && <div className="loading-text">Loading videos...</div>}

          {!loading && videos.length === 0 && (
            <div className="empty-state">
              <p>No videos added yet. Add your first video above!</p>
            </div>
          )}

          {!loading && videos.length > 0 && (
            <div className="videos-grid">
              {videos.map((video) => (
                <div key={video.id} className="video-item">
                  <div className="video-thumbnail">
                    {isYouTubeUrl(video.url) ? (
                      <iframe
                        src={convertToEmbedUrl(video.url)}
                        title={video.title}
                        style={{ width: '100%', height: '150px', borderRadius: '8px', border: 'none' }}
                        allow="autoplay; encrypted-media"
                      />
                    ) : (
                      <video
                        src={video.url}
                        muted
                        loop
                        style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                    )}
                  </div>
                  <div className="video-details">
                    <h3>{video.title}</h3>
                    {video.description && <p>{video.description}</p>}
                  </div>
                  <div className="video-actions">
                    <button onClick={() => handleEdit(video)} className="btn-edit">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(video.id)} className="btn-delete">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageVideos;
